import * as React from 'react';
import { Helmet } from 'react-helmet';
import { Redirect, withRouter } from 'react-router-dom';

import Loader from 'components/atoms/Loader';
import Card from 'containers/molecules/Card/Pool';

import { getBaseTitle } from 'defs/routes';
import { TrackingDescriptionIDType } from 'defs/tracking';

import i18n from 'helper/i18n';
import { compileDynamicRoute, template } from 'helper/string';
import { delayed } from 'lib/Job';

import { ProfileDataType } from 'store/profile';

import { PoolEnumType } from 'types/services/magnolia/enums/PoolEnum';
import { ProgramItemDtoType } from 'types/services/spring/dtos/ProgramItemDto';
import { WithRouterType } from 'types/withRouter';

export type PoolDetailRendererPropsType = WithRouterType & {
	isAuthenticated: boolean;
	isUserLoaded: boolean;
	content?: ProgramItemDtoType;
	userData?: ProfileDataType;
	programItemRef?: string;
	activeOverlayRef?: string;
	selectedPoolID?: PoolEnumType;
	selectedPoolURL: string;
	selectedItemID: string;
	contentType: string;
	currentWeight?: number;
	doLoadSelectedFavoriteData: (id: string, contentType: string) => void;
	doLoadSelectedItem: (id: string, contentType: string) => void;
	doRequestMeasurement: () => void;
	doAddEvents: (...events: Array<Object>) => void;
	doOpenDescription: (guideId: TrackingDescriptionIDType) => void;
};

class PoolDetailRenderer extends React.Component<PoolDetailRendererPropsType> {
	public static displayName = 'Pool(DetailRenderer)';

	public componentDidMount() {
		const { doLoadSelectedItem, doLoadSelectedFavoriteData, selectedItemID, contentType } = this.props;

		if (contentType && selectedItemID) {
			delayed(() => {
				doLoadSelectedItem(selectedItemID, contentType);
				doLoadSelectedFavoriteData(contentType.toUpperCase(), selectedItemID);
			}, 500);
		}
	}

	get seoData(): { title: string; ogTitle: string; description: string } | void {
		const { content } = this.props;

		if (!content) {
			return void 0;
		}

		return {
			title: template(i18n(`react.impulsCoach.routes.poolDetail.title`), {
				...content,
			}),
			ogTitle: template(i18n(`react.impulsCoach.routes.poolDetail.ogTitle`), {
				...content,
			}),
			description: template(i18n(`react.impulsCoach.routes.poolDetail.description`), {
				...content,
			}),
		};
	}

	public render() {
		const { contentType, selectedItemID, content, history, selectedPoolURL } = this.props;
		const seoData = this.seoData;
		const redirectOnError = compileDynamicRoute(i18n('react.impulsCoach.routes.pool.cluster.href'), {
			selectedPool: selectedPoolURL,
			cluster: i18n('react.impulsCoach.routes.pool.cluster.all.href'),
		});

		if (!contentType || !selectedItemID) {
			return <Redirect to={redirectOnError} />;
		}

		if (!content) {
			return <Loader />;
		}

		if (typeof content === 'object' && Object.keys(content).length === 0) {
			return <Redirect to={redirectOnError} />;
		}

		return (
			<React.Fragment>
				{seoData && (
					<Helmet>
						<title>
							{seoData.title} {getBaseTitle()}
						</title>
						<meta property="og:title" content={seoData.title} />
						<meta name="description" content={seoData.description} />
					</Helmet>
				)}
				<Card {...(content as any)} useStaticSelectedContent history={history} />
			</React.Fragment>
		);
	}
}

export default withRouter(PoolDetailRenderer as any);
